import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { Badge } from './ui/badge';
import { Loader2, Plus, Power, QrCode, RefreshCw, Check, Trash, List, Users, Pen, MessageSquare, Building2, Webhook, Star, Lock, LockOpen, Workflow } from 'lucide-react';
import { SelectWithSearch } from './ui/select-with-search';
import { Switch } from './ui/switch';
import { instanciaService } from '../services/instanciaService';
import { filaService } from '../services/filaService';
import { usuarioService } from '../services/usuarioService';
import MetaTemplateManager from './instancias/MetaTemplateManager';
import MetaBusinessProfileDialog from './instancias/MetaBusinessProfileDialog';
import MetaWebhookDialog from './instancias/MetaWebhookDialog';
import { authService } from '../services/authService';
import { SelectOption } from '../types';
import MetaInstanceForm from './instancias/MetaInstanceForm';
import MetaEmbeddedSignup from './instancias/MetaEmbeddedSignup';
import HintTooltip from './common/HintTooltip';

// Configuração da API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Usuario {
  id: string;
  nome: string;
  email: string;
}

interface Fila {
  id: string;
  nome: string;
  descricao?: string;
}

interface Instancia {
  id: string;
  nome: string;
  status?: string;
  statusConexao?: string;
  dataCriacao: string;
  ultimaConexao?: string;
  apiEndpoint?: string;
  webhookUrl?: string;
  events?: string[];
  filas?: Fila[];
  usuarios?: Usuario[];
  tipoApi?: 'UAZAPI' | 'META';
  phoneNumberId?: string;
  wabaId?: string;
  accessToken?: string;
  appSecret?: string;
  displayName?: string;
  displayPhoneNumber?: string;
  instanciaUazapiId?: string;
  isOnBizApp?: boolean;
  padrao?: boolean;
  // Instância confidencial (só o SuperAdmin altera): conversas só para vinculados, diretores e SuperAdmin
  confidencial?: boolean;
  // Instância confidencial só fala com o N8N com esta flag (só o SuperAdmin altera)
  n8nConfidencial?: boolean;
}

const InstanceApiConnection = () => {
  const { t } = useTranslation();
  const [instancias, setInstancias] = useState<Instancia[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [novaInstancia, setNovaInstancia] = useState('');
  const [tipoApi, setTipoApi] = useState<'UAZAPI' | 'META'>('UAZAPI');
  const [metaMode, setMetaMode] = useState<'auto' | 'manual'>('auto');
  const [metaConfig, setMetaConfig] = useState<any>({});
  const [apiUrl, setApiUrl] = useState<string>('');
  const [webhookUrl, setWebhookUrl] = useState<string>('');

  // Atualizar URLs e chaves quando o tipo de API mudar
  useEffect(() => {
    // if (tipoApi === 'EVOLUTION') {
    //   setApiUrl(process.env.REACT_APP_EVOLUTION_API_URL || '');
    //   setWebhookUrl(process.env.REACT_APP_EVOLUTION_WEBHOOK_URL || '');
    // } else {
    setApiUrl(process.env.REACT_APP_UAZAPI_API_URL || '');
    setWebhookUrl(process.env.REACT_APP_UAZAPI_WEBHOOK_URL || '');
    // }
  }, []);
  const [qrCodeDialog, setQrCodeDialog] = useState(false);
  const [currentQrCode, setCurrentQrCode] = useState<string | undefined>(undefined);
  const [currentInstanceId, setCurrentInstanceId] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [editingInstance, setEditingInstance] = useState<Instancia | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // State for queues and users
  const [queues, setQueues] = useState<Fila[]>([]);
  const [users, setUsers] = useState<Usuario[]>([]);
  // State to handle recreation when Evolution instance was deleted (404)
  const [openRecreateDialog, setOpenRecreateDialog] = useState(false);
  const [recreateInstanceId, setRecreateInstanceId] = useState<string | null>(null);
  const [isRecreating, setIsRecreating] = useState(false);
  const [templateManagerInstanceId, setTemplateManagerInstanceId] = useState<string | null>(null);
  const [businessProfileInstanceId, setBusinessProfileInstanceId] = useState<string | null>(null);
  const [webhookInstanceId, setWebhookInstanceId] = useState<string | null>(null);
  const user = authService.getCurrentUser();

  // State for selected queues and users (storing only the selected values as SelectOption)
  const [selectedQueues, setSelectedQueues] = useState<SelectOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>([]);

  // Convert queues to SelectOption[] for the dropdown
  const queueOptions = useMemo((): SelectOption[] =>
    queues.map(queue => ({
      value: queue.id,
      label: queue.nome || 'Sem nome',
      ...queue
    })),
    [queues]
  );

  // Convert users to SelectOption[] for the dropdown
  const userOptions = useMemo((): SelectOption[] =>
    users.map(user => ({
      value: user.id,
      label: user.nome ? `${user.nome}${user.email ? ` (${user.email})` : ''}` : t('channels.unnamedUser'),
      ...user
    })),
    [users, t]
  );

  const defaultEvents = [
    'QRCODE_UPDATED',
    'MESSAGES_UPSERT',
    'MESSAGES_UPDATE',
    'CONNECTION_UPDATE',
    'CONTACTS_UPSERT',
    'CONTACTS_UPDATE',
    'PRESENCE_UPDATE'
  ] as const;
  type EventName = typeof defaultEvents[number];
  const [eventSwitches, setEventSwitches] = useState<Record<EventName, boolean>>({
    QRCODE_UPDATED: true,
    MESSAGES_UPSERT: true,
    MESSAGES_UPDATE: true,
    CONNECTION_UPDATE: true,
    CONTACTS_UPSERT: true,
    CONTACTS_UPDATE: true,
    PRESENCE_UPDATE: true
  });

  // Fetch all instances
  const fetchInstancias = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await instanciaService.listar();
      const instanciasData = Array.isArray(response?.data?.instancias) ? response?.data?.instancias : [];

      setInstancias(instanciasData);
      instanciasData.forEach((instancia: any) => {
        fetchInstanceStatus(instancia.id);
      });
    } catch (err: any) {
      console.error('Erro ao buscar instâncias:', err);
      showToast(t('common.error'), t('channels.toast.loadError'), 'destructive');
    } finally {
      setIsLoading(false);
    }
  }, [toast, t]);

  // Carregar filas e usuários uma única vez
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Carregar filas
        const filasResponse = await filaService.listar();
        const filasData = Array.isArray(filasResponse?.data?.filas) ? filasResponse?.data?.filas : [];
        setQueues(filasData);

        // Carregar usuários
        const usuariosResponse = await usuarioService.listar();
        const usuariosData = Array.isArray(usuariosResponse?.data?.usuarios) ? usuariosResponse?.data?.usuarios : [];
        setUsers(usuariosData);

        // If editing, update selected queues and users with full objects
        if (editingInstance) {
          if (editingInstance.filas && editingInstance.filas.length > 0) {
            const selectedQueueOptions = Array.isArray(editingInstance.filas) ? editingInstance.filas.map((f: any) => f.filaId) : []
            setSelectedQueues(selectedQueueOptions);
          }

          if (editingInstance.usuarios && editingInstance.usuarios.length > 0) {
            const selectedUserOptions = Array.isArray(editingInstance.usuarios) ? editingInstance.usuarios.map((u: any) => u.usuarioId) : []
            setSelectedUsers(selectedUserOptions);
          }
        }
      } catch (err: any) {
        console.error('Erro ao carregar dados iniciais:', err);
        showToast(t('common.error'), t('channels.toast.initialLoadError'), 'destructive');
      }
    };

    loadInitialData();
  }, []);

  // Reset form fields when dialog is opened/closed
  useEffect(() => {
    if (!openDialog) {
      setNovaInstancia('');
      setSelectedQueues([]);
      setSelectedUsers([]);
      setApiUrl(process.env.REACT_APP_UAZAPI_API_URL || '');
      setWebhookUrl(process.env.REACT_APP_UAZAPI_WEBHOOK_URL || '');
      setEventSwitches({
        QRCODE_UPDATED: true,
        MESSAGES_UPSERT: true,
        MESSAGES_UPDATE: true,
        CONNECTION_UPDATE: true,
        CONTACTS_UPSERT: true,
        CONTACTS_UPDATE: true,
        PRESENCE_UPDATE: true
      });
      setIsEditing(false);
      setEditingInstance(null);
      setMetaConfig({});
      setMetaMode('auto');
    }
  }, [openDialog]);

  const handleEditInstance = (instancia: Instancia) => {
    setEditingInstance(instancia);
    setNovaInstancia(instancia.nome);
    setApiUrl(instancia.apiEndpoint || '');
    setWebhookUrl(instancia.webhookUrl || '');

    // Definir o tipo de API da instância
    setTipoApi(instancia.tipoApi || 'UAZAPI');
    
    // Set meta config se for META
    if (instancia.tipoApi === 'META') {
      setMetaMode('manual'); // Ao editar, forçamos o modo manual para ver os dados
      setMetaConfig({
        phoneNumberId: instancia.phoneNumberId || '',
        wabaId: instancia.wabaId || '',
        accessToken: instancia.accessToken || '',
        appSecret: instancia.appSecret || '',
        displayName: instancia.displayName || '',
        instanciaUazapiId: instancia.instanciaUazapiId || ''
      });
    } else {
      setMetaConfig({});
    }

    // Set selected queues with full objects when available
    setSelectedQueues(
      Array.isArray(instancia.filas)
        ? instancia.filas.map((f: any) => f.filaId || f.id)
        : []
    );

    // Set selected users with full objects when available
    setSelectedUsers(
      Array.isArray(instancia.usuarios)
        ? instancia.usuarios.map((u: any) => u.usuarioId || u.id)
        : []
    );

    // Set event switches
    if (instancia.events) {
      const newEventSwitches = { ...eventSwitches };
      instancia.events.forEach((event: string) => {
        if (event in eventSwitches) {
          newEventSwitches[event as keyof typeof eventSwitches] = true;
        }
      });
      setEventSwitches(newEventSwitches);
    } else {
      // Reset all event switches if no events
      setEventSwitches({
        QRCODE_UPDATED: true,
        MESSAGES_UPSERT: true,
        MESSAGES_UPDATE: true,
        CONNECTION_UPDATE: true,
        CONTACTS_UPSERT: true,
        CONTACTS_UPDATE: true,
        PRESENCE_UPDATE: true
      });
    }
    setOpenDialog(true);
  };

  const handleUpdateInstance = async () => {
    if (!editingInstance) return;
    const selectedEvents = (Object.entries(eventSwitches)
      .filter(([_, value]) => value)
      .map(([key]) => key)) as EventName[];

    // Helper function to safely extract IDs from arrays of mixed types
    const extractIds = (items: any[]): string[] => {
      if (!items || !Array.isArray(items)) return [];
      return items.map(item => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          if ('id' in item && item.id) return item.id;
          if ('value' in item && item.value) return item.value;
        }
        return '';
      }).filter(Boolean);
    };

    console.log('Atualizando instância com usuárioIds:', extractIds(selectedUsers));
    try {
      await instanciaService.atualizar(editingInstance.id, {
        nome: novaInstancia,
        webhookUrl: tipoApi === 'UAZAPI'
          ? (process.env.REACT_APP_UAZAPI_WEBHOOK_URL || undefined)
          : (process.env.REACT_APP_EVOLUTION_WEBHOOK_URL || undefined),
        events: selectedEvents,
        filaIds: extractIds(selectedQueues),
        usuarioIds: extractIds(selectedUsers),
        tipoApi: tipoApi,
        ...(tipoApi === 'META' ? metaConfig : {})
      });

      await fetchInstancias();

      // Dispatch event to refresh Header instances
      window.dispatchEvent(new CustomEvent('refreshInstances'));

      showToast(t('common.success'), t('channels.toast.updated'), "default");
      setEditingInstance(null);
      setOpenDialog(false);
    } catch (error: any) {
      console.error('Error updating instance:', error);
      showToast(t('common.error'), error.response?.data?.message || t('channels.toast.updateError'), "destructive");
    } finally {
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  // Fetch instance status
  const fetchInstanceStatus = useCallback(async (instanceId: string) => {
    try {
      const response: any = await instanciaService.verificarStatus(instanceId);
      setInstancias(prev =>
        prev.map(i =>
          i.id === instanceId
            ? {
              ...i,
              statusConexao: response.data.statusConexao || response.data.status || 'disconnected',
              ultimaConexao: response.data.ultimaConexao || i.ultimaConexao,
            }
            : i
        )
      );

      // Close QR code dialog if instance is connected
      const respStatus = (response?.data?.statusConexao || response?.data?.status || '').toLowerCase();
      if (currentInstanceId === instanceId && respStatus === 'connected') {
        setQrCodeDialog(false);
        setCurrentInstanceId(null);
        await fetchInstancias();

        // Dispatch event to refresh Header instances
        window.dispatchEvent(new CustomEvent('refreshInstances'));
      }
    } catch (error) {
      const resp: any = (error as any)?.response;
      const status = resp?.status;
      const code = resp?.data?.code;
      if (status === 404 && code === 'EVOLUTION_INSTANCE_NOT_FOUND') {
        // Evolution instance likely deleted -> propose automatic recreation
        setRecreateInstanceId(instanceId);
        setOpenRecreateDialog(true);
      }
    }
  }, [currentInstanceId]);

  // Confirm recreation flow
  const handleRecreateConfirm = async () => {
    if (!recreateInstanceId) return;
    setIsRecreating(true);
    try {
      await instanciaService.recriar(recreateInstanceId);
      showToast(t('common.success'), t('channels.toast.recreated'));
      // Optionally auto-connect
      await fetchInstancias();

      // Dispatch event to refresh Header instances
      window.dispatchEvent(new CustomEvent('refreshInstances'));

      await handleConnectInstance(recreateInstanceId);
    } catch (err: any) {
      console.error('Erro ao recriar instância:', err);
      showToast(t('common.error'), err.response?.data?.message || t('channels.toast.recreateError'), 'destructive');
    } finally {
      setIsRecreating(false);
      setOpenRecreateDialog(false);
      setRecreateInstanceId(null);
    }
  };

  // Show toast notification
  const showToast = (title: string, description: string, variant: 'default' | 'destructive' = 'default') => {
    toast({
      title,
      description,
      variant
    });
  };

  // Create new instance
  // `overrides` é usado pelo fluxo Automático da Meta: o botão "Conectar" já cria
  // a instância, sem passar pelo formulário manual. O estado do React ainda não
  // teria os dados recém-recebidos, por isso eles vêm por parâmetro.
  const handleCreateInstance = async (overrides?: { meta?: any; nome?: string }) => {
    const nomeFinal = (overrides?.nome ?? novaInstancia).trim();
    if (!nomeFinal) {
      showToast(t('common.warning'), t('channels.toast.nameRequired'), 'destructive');
      return;
    }
    const metaFinal = overrides?.meta ? { ...metaConfig, ...overrides.meta } : metaConfig;

    const selectedEvents = (Object.entries(eventSwitches)
      .filter(([_, value]) => value)
      .map(([key]) => key)) as EventName[];

    // Extract IDs from selected queues and users, handling both Fila/Usuario and SelectOption types
    const getQueueId = (q: Fila | SelectOption) => 'id' in q ? q.id : q.value;
    const getUserId = (u: Usuario | SelectOption) => 'id' in u ? u.id : u.value;

    const requestData = {
      nome: nomeFinal,
      apiEndpoint: apiUrl || undefined,
      webhookUrl: tipoApi === 'UAZAPI'
        ? (process.env.REACT_APP_UAZAPI_WEBHOOK_URL || undefined)
        : (process.env.REACT_APP_EVOLUTION_WEBHOOK_URL || undefined),
      events: selectedEvents,
      filaIds: selectedQueues.map(getQueueId),
      usuarioIds: selectedUsers.map(getUserId),
      tipoApi: tipoApi,
      ...(tipoApi === 'META' ? metaFinal : {})
    };

    setIsCreating(true);
    try {
      // Create the instance with all required data
      const response = await instanciaService.criar(requestData);

      // Reset form
      setOpenDialog(false);
      setNovaInstancia('');
      setSelectedQueues([]);
      setSelectedUsers([]);
      setApiUrl(process.env.REACT_APP_UAZAPI_API_URL || '');
      setWebhookUrl(process.env.REACT_APP_UAZAPI_WEBHOOK_URL || '');
      setTipoApi('UAZAPI');
      setEventSwitches({
        QRCODE_UPDATED: true,
        MESSAGES_UPSERT: true,
        MESSAGES_UPDATE: true,
        CONNECTION_UPDATE: true,
        CONTACTS_UPSERT: true,
        CONTACTS_UPDATE: true,
        PRESENCE_UPDATE: true
      });
      await fetchInstancias();

      // Dispatch event to refresh Header instances
      window.dispatchEvent(new CustomEvent('refreshInstances'));

      // Auto-connect after creation
      if (response?.data?.id) {
        await handleConnectInstance(response.data.id);
      }

      showToast(t('common.success'), t('channels.toast.created'));
    } catch (err: any) {
      console.error('Erro ao criar instância:', err);
      // O backend devolve a mensagem de erro como string pura nesse endpoint
      const data = err.response?.data;
      const detalhe = typeof data === 'string' ? data : (data?.errors?.join(' ') || data?.message);
      showToast(t('common.error'), detalhe || t('channels.toast.createError'), 'destructive');
      // Falhou na criação automática (fluxo Meta): cai para o formulário, já
      // preenchido, para o usuário corrigir e tentar de novo.
      if (overrides?.meta) setMetaMode('manual');
    } finally {
      setIsCreating(false);
    }
  };

  // Connect to instance and show QR code
  const handleConnectInstance = async (id: string) => {
    setIsConnecting(id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/instancias/${id}/connect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const qrCode = response.data?.qrCode;
      if (qrCode) {
        setCurrentQrCode(qrCode);
        setCurrentInstanceId(id);
        setQrCodeDialog(true);
      } else {
        showToast(t('common.warning'), t('channels.toast.qrUnavailable'), 'destructive');
      }
    } catch (err: any) {
      console.error('Erro ao conectar instância:', err);
      showToast(t('common.error'), err.response?.data?.message || t('channels.toast.connectError'), 'destructive');
    } finally {
      setIsConnecting(null);
    }
  };

  // Disconnect instance
  // const handleDisconnectInstance = async (id: string) => {
  //   setIsDisconnecting(id);
  //   try {
  //     const token = localStorage.getItem('token');
  //     await axios.post(
  //       `${API_URL}/instancias/${id}/disconnect`,
  //       {},
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );

  //     await fetchInstancias();
  //     showToast('Sucesso', 'Instância desconectada com sucesso!');
  //   } catch (err: any) {
  //     console.error('Erro ao desconectar instância:', err);
  //     showToast('Erro', err.response?.data?.message || 'Erro ao desconectar instância', 'destructive');
  //   } finally {
  //     setIsDisconnecting(null);
  //   }
  // };

  // Definir instância padrão da empresa
  const [isSettingDefault, setIsSettingDefault] = useState<string | null>(null);
  const handleSetDefault = async (id: string) => {
    setIsSettingDefault(id);
    try {
      await instanciaService.definirPadrao(id);
      setInstancias(prev => prev.map(i => ({ ...i, padrao: i.id === id })));
      showToast(t('common.success'), t('channels.toast.defaultSet'));
    } catch (err: any) {
      console.error('Erro ao definir instância padrão:', err);
      showToast(t('common.error'), err.response?.data?.message || t('channels.toast.defaultError'), 'destructive');
    } finally {
      setIsSettingDefault(null);
    }
  };

  // Instância confidencial (issue #26): só o SuperAdmin liga/desliga
  const [isSettingConfidential, setIsSettingConfidential] = useState<string | null>(null);
  const handleToggleConfidential = async (instancia: Instancia) => {
    const novo = !instancia.confidencial;
    if (!window.confirm(novo ? t('channels.confidentialConfirmOn') : t('channels.confidentialConfirmOff'))) return;
    setIsSettingConfidential(instancia.id);
    try {
      await instanciaService.definirConfidencial(instancia.id, novo);
      setInstancias(prev => prev.map(i => (i.id === instancia.id ? { ...i, confidencial: novo } : i)));
      showToast(t('common.success'), novo ? t('channels.toast.confidentialOn') : t('channels.toast.confidentialOff'));
    } catch (err: any) {
      console.error('Erro ao definir instância confidencial:', err);
      showToast(t('common.error'), err.response?.data?.message || t('channels.toast.confidentialError'), 'destructive');
    } finally {
      setIsSettingConfidential(null);
    }
  };

  // Integração N8N da instância confidencial (issue #26): só o SuperAdmin libera
  const [isSettingN8nConfidential, setIsSettingN8nConfidential] = useState<string | null>(null);
  const handleToggleN8nConfidential = async (instancia: Instancia) => {
    const novo = !instancia.n8nConfidencial;
    if (!window.confirm(novo ? t('channels.n8nConfidentialConfirmOn') : t('channels.n8nConfidentialConfirmOff'))) return;
    setIsSettingN8nConfidential(instancia.id);
    try {
      await instanciaService.definirN8nConfidencial(instancia.id, novo);
      setInstancias(prev => prev.map(i => (i.id === instancia.id ? { ...i, n8nConfidencial: novo } : i)));
      showToast(t('common.success'), novo ? t('channels.toast.n8nConfidentialOn') : t('channels.toast.n8nConfidentialOff'));
    } catch (err: any) {
      console.error('Erro ao definir integração N8N da instância confidencial:', err);
      showToast(t('common.error'), err.response?.data?.message || t('channels.toast.n8nConfidentialError'), 'destructive');
    } finally {
      setIsSettingN8nConfidential(null);
    }
  };

  // Delete instance
  const handleDeleteInstance = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/instancias/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchInstancias();
      showToast(t('common.success'), t('channels.toast.deleted'));
    } catch (err: any) {
      console.error('Erro ao excluir instância:', err);
      showToast(t('common.error'), err.response?.data?.message || t('channels.toast.deleteError'), 'destructive');
    }
  };

  // Refresh QR code
  const handleRefreshQrCode = async () => {
    if (!currentInstanceId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/instancias/${currentInstanceId}/connect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const qrCode = response.data?.qrCode;
      if (qrCode) {
        setCurrentQrCode(qrCode);
      } else {
        showToast(t('common.warning'), t('channels.toast.qrUnavailable'), 'destructive');
      }
    } catch (err: any) {
      console.error('Erro ao atualizar QR Code:', err);
      showToast(t('common.error'), t('channels.toast.qrRefreshError'), 'destructive');
    }
  };

  // Get status badge variant
  const getStatusVariant = (status: string | undefined) => {
    const statusValue = status?.toLowerCase();
    switch (statusValue) {
      case 'connected':
        return 'default';
      case 'disconnected':
        return 'destructive';
      case 'connecting':
      case 'qrcode':
        return 'outline';
      default:
        return 'outline';
    }
  };

  // Get status display text
  const getStatusText = (status: string | undefined) => {
    const statusValue = status?.toLowerCase();
    switch (statusValue) {
      case 'connected':
        return t('channels.status.connected');
      case 'disconnected':
        return t('channels.status.disconnected');
      case 'connecting':
        return t('channels.status.connecting');
      case 'qrcode':
        return t('channels.status.qrcode');
      default:
        return t('channels.status.unknown');
    }
  };

  // Load instances on mount
  useEffect(() => {
    fetchInstancias();
  }, [fetchInstancias]);

  useEffect(() => {
    if (selectedInstanceId) {
      setOpenDeleteDialog(true);
    }
  }, [selectedInstanceId]);

  // Auto-close QR modal when the current instance becomes connected
  useEffect(() => {
    if (!qrCodeDialog || !currentInstanceId) return;
    const inst = instancias.find((i) => i.id === currentInstanceId);
    const status = (inst?.status || inst?.statusConexao || '').toLowerCase();
    if (status === 'connected') {
      setQrCodeDialog(false);
      setCurrentInstanceId(null);
      fetchInstancias();
    }
  }, [instancias, qrCodeDialog, currentInstanceId, fetchInstancias]);

  // Poll instance status while QR modal is open to auto-close on connect
  useEffect(() => {
    if (!qrCodeDialog || !currentInstanceId) return;

    const interval = setInterval(() => {
      fetchInstanceStatus(currentInstanceId);
    }, 3000);

    return () => clearInterval(interval);
  }, [qrCodeDialog, currentInstanceId, fetchInstanceStatus]);

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedInstanceId) return;

    try {
      setIsDeleting(selectedInstanceId);
      await handleDeleteInstance(selectedInstanceId);
    } finally {
      setOpenDeleteDialog(false);
      setSelectedInstanceId(null);
      setIsDeleting(null);
    }
  };

  // Loading state
  if (isLoading && instancias.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('channels.title')}</h1>
          <p className="text-muted-foreground">{t('channels.subtitle')}</p>
        </div>
        <HintTooltip text={t('channels.hints.newInstance')} side="left">
          <Button onClick={() => setOpenDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('channels.newInstance')}
          </Button>
        </HintTooltip>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {instancias.map((instancia) => {
          const status = instancia.status || instancia.statusConexao || 'disconnected';
          const isConnected = status === 'connected';
          const isConnectingThis = isConnecting === instancia.id;
          const isDisconnectingThis = isDisconnecting === instancia.id;
          const isDeletingThis = isDeleting === instancia.id;

          return (
            <Card
              key={instancia.id}
              className={`overflow-hidden dark:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-shadow
                ${isConnected
                  ? 'ring-2 ring-green-500'
                  : ['connecting', 'qrcode'].includes((status || '').toLowerCase())
                    ? 'ring-2 ring-yellow-500'
                    : 'ring-2 ring-red-500'}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {instancia.nome}
                      {instancia.padrao && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300"
                          title={t('channels.hints.isDefault')}
                        >
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-500" />
                          {t('channels.defaultBadge')}
                        </span>
                      )}
                      {instancia.confidencial && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300"
                          title={t('channels.hints.confidential')}
                        >
                          <Lock className="h-3 w-3" />
                          {t('channels.confidentialBadge')}
                        </span>
                      )}
                      {instancia.confidencial && instancia.n8nConfidencial && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300"
                          title={t('channels.hints.n8nConfidential')}
                        >
                          <Workflow className="h-3 w-3" />
                          {t('channels.n8nConfidentialBadge')}
                        </span>
                      )}
                    </CardTitle>
                    {instancia.tipoApi === 'META' && instancia.displayPhoneNumber && (
                      <p className="text-sm text-muted-foreground font-mono mt-1">
                        {instancia.displayPhoneNumber}
                      </p>
                    )}
                    {instancia.tipoApi === 'META' && instancia.isOnBizApp && (
                      <span
                        className="inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                        title={t('channels.coexistenceTooltip')}
                      >
                        {t('channels.coexistenceBadge')}
                      </span>
                    )}
                  </div>
                  <Badge
                    variant={getStatusVariant(status)}
                    className={`capitalize ${isConnected ? 'bg-green-500' : ['connecting', 'qrcode'].includes((status || '').toLowerCase()) ? 'bg-yellow-500' : 'bg-red-500'}`}
                  >
                    {getStatusText(status)}
                  </Badge>
                </div>
              </CardHeader>
              {instancia.ultimaConexao && (
                <CardContent className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-x-2 text-sm">
                    <span className="text-muted-foreground">{t('channels.createdAt')}</span>
                    <span>{new Date(instancia.dataCriacao).toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-x-2 text-sm">
                    <span className="text-muted-foreground">{t('channels.lastConnection')}</span>
                    <span>{new Date(instancia.ultimaConexao).toLocaleString()}</span>
                  </div>
                </CardContent>
              )}
              <CardFooter className="flex flex-wrap justify-end gap-2 pt-2">
                {instancia.tipoApi === 'META' ? (
                  <>
                    <HintTooltip text={t('channels.hints.manageTemplates')}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="dark:text-white dark:bg-slate-700"
                        onClick={() => setTemplateManagerInstanceId(instancia.id)}
                        aria-label={t('channels.manageTemplates')}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </HintTooltip>
                    <HintTooltip text={t('channels.hints.businessProfile')}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="dark:text-white dark:bg-slate-700"
                        onClick={() => setBusinessProfileInstanceId(instancia.id)}
                        aria-label={t('channels.businessProfile')}
                      >
                        <Building2 className="h-4 w-4" />
                      </Button>
                    </HintTooltip>
                    {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
                      <HintTooltip text={t('channels.hints.metaWebhook')}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="dark:text-white dark:bg-slate-700"
                          onClick={() => setWebhookInstanceId(instancia.id)}
                          aria-label={t('channels.metaWebhook')}
                        >
                          <Webhook className="h-4 w-4" />
                        </Button>
                      </HintTooltip>
                    )}
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="dark:text-white dark:bg-slate-700"
                    onClick={() => handleConnectInstance(instancia.id)}
                    disabled={isConnectingThis || isConnected}
                  >
                    {isConnectingThis ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <QrCode className="h-4 w-4" />
                    )}
                    {isConnected ? '' : t('common.connect')}
                  </Button>
                )}
                {user?.role === 'SuperAdmin' && (
                  <HintTooltip text={instancia.confidencial ? t('channels.hints.confidentialOff') : t('channels.hints.confidentialOn')}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="dark:text-white dark:bg-slate-700"
                      onClick={() => handleToggleConfidential(instancia)}
                      disabled={isSettingConfidential === instancia.id}
                      aria-label={t('channels.confidentialBadge')}
                    >
                      {isSettingConfidential === instancia.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : instancia.confidencial ? (
                        <Lock className="h-4 w-4 text-purple-600" />
                      ) : (
                        <LockOpen className="h-4 w-4" />
                      )}
                    </Button>
                  </HintTooltip>
                )}
                {user?.role === 'SuperAdmin' && instancia.confidencial && (
                  <HintTooltip text={instancia.n8nConfidencial ? t('channels.hints.n8nConfidentialOff') : t('channels.hints.n8nConfidentialOn')}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="dark:text-white dark:bg-slate-700"
                      onClick={() => handleToggleN8nConfidential(instancia)}
                      disabled={isSettingN8nConfidential === instancia.id}
                      aria-label={t('channels.n8nConfidentialBadge')}
                    >
                      {isSettingN8nConfidential === instancia.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Workflow className={`h-4 w-4 ${instancia.n8nConfidencial ? 'text-orange-600' : 'text-muted-foreground'}`} />
                      )}
                    </Button>
                  </HintTooltip>
                )}
                {(!instancia.confidencial || user?.role === 'SuperAdmin') && (
                <HintTooltip text={instancia.padrao ? t('channels.hints.isDefault') : t('channels.hints.setDefault')}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="dark:text-white dark:bg-slate-700"
                    onClick={() => !instancia.padrao && handleSetDefault(instancia.id)}
                    disabled={isSettingDefault === instancia.id}
                    aria-label={t('channels.setDefault')}
                  >
                    {isSettingDefault === instancia.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Star className={`h-4 w-4 ${instancia.padrao ? 'fill-yellow-400 text-yellow-500' : ''}`} />
                    )}
                  </Button>
                </HintTooltip>
                )}
                {/* Instância confidencial: alterar/excluir só o SuperAdmin */}
                {(!instancia.confidencial || user?.role === 'SuperAdmin') && (<>
                <HintTooltip text={t('channels.hints.edit')}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="dark:text-white dark:bg-slate-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditInstance(instancia);
                    }}
                    disabled={isConnectingThis || isDisconnectingThis}
                    aria-label={t('common.edit')}
                  >
                    <Pen className="h-4 w-4" />
                  </Button>
                </HintTooltip>
                {/* <Button
                  variant={isConnected ? 'destructive' : 'outline'}
                  size="sm"
                  className="dark:text-white dark:bg-slate-700"
                  onClick={() => isConnected
                    ? handleDisconnectInstance(instancia.id)
                    : handleConnectInstance(instancia.id)
                  }
                  disabled={isDisconnectingThis || isConnectingThis}
                >
                  {isDisconnectingThis ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Power className="h-4 w-4" />
                  )}
                  {isConnected ? 'Desativar' : 'Ativar'}
                </Button> */}
                <HintTooltip text={t('channels.hints.delete')}>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="dark:text-white"
                    onClick={() => setSelectedInstanceId(instancia.id)}
                    disabled={isDeletingThis}
                    aria-label={t('common.delete')}
                  >
                    {isDeletingThis ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash className="h-4 w-4" />
                    )}
                  </Button>
                </HintTooltip>
                </>)}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {instancias.length === 0 && !isLoading && (
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 text-center">
          <p className="text-muted-foreground">
            {t('channels.noInstancesFound')}
          </p>
        </div>
      )}

      <Dialog open={openDeleteDialog} onOpenChange={(open) => {
        if (!open) {
          setOpenDeleteDialog(false);
          setSelectedInstanceId(null);
        }
      }}>
        <DialogContent className="dark:bg-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">{t('channels.confirmDeleteTitle')}</DialogTitle>
            <DialogDescription className="dark:text-white">
              {t('channels.confirmDeleteDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="dark:text-white dark:bg-slate-700" onClick={() => {
              setOpenDeleteDialog(false);
              setSelectedInstanceId(null);
            }} disabled={isDeleting === selectedInstanceId}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting === selectedInstanceId}>
              {isDeleting === selectedInstanceId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recreate Evolution Instance Dialog (404 handling) */}
      <Dialog open={openRecreateDialog} onOpenChange={(open) => {
        if (!open && !isRecreating) {
          setOpenRecreateDialog(false);
          setRecreateInstanceId(null);
        }
      }}>
        <DialogContent className="dark:bg-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">{t('channels.instanceNotFoundTitle')}</DialogTitle>
            <DialogDescription className="dark:text-white">
              {t('channels.instanceNotFoundDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="dark:text-white dark:bg-slate-700"
              onClick={() => {
                if (!isRecreating) {
                  setOpenRecreateDialog(false);
                  setRecreateInstanceId(null);
                }
              }}
              disabled={isRecreating}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRecreateConfirm} disabled={isRecreating}>
              {isRecreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('channels.recreating')}
                </>
              ) : (
                t('channels.recreateAuto')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Instance Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        {/* Clicar fora (ou Esc) não fecha: no fluxo Meta isso descartava o
            login já feito na janela da Meta. Fecha só por Cancelar/X. */}
        <DialogContent
          className="dark:bg-gray-800"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="dark:text-gray-200">{t('channels.newInstanceDialogTitle')}</DialogTitle>
            <DialogDescription className="dark:text-gray-200">
              {t('channels.newInstanceDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Tipo de API */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label htmlFor="tipoApi" className="sm:text-right dark:text-gray-200">
                {t('channels.apiTypeLabel')}
              </Label>
              <select
                id="tipoApi"
                value={tipoApi}
                onChange={(e) => setTipoApi(e.target.value as 'UAZAPI' | 'META')}
                className="sm:col-span-3 dark:bg-gray-200 dark:text-gray-900 px-3 py-2 border rounded-md"
                disabled={isCreating || isEditing || editingInstance !== null}
              >
                <option value="UAZAPI">UAZAPI</option>
                <option value="META">{t('channels.metaOfficialApi')}</option>
              </select>
            </div>

            {/* Nome */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label htmlFor="instanceName" className="sm:text-right dark:text-gray-200">
                {t('channels.nameLabel')}
              </Label>
              <Input
                id="instanceName"
                value={novaInstancia}
                onChange={(e) => setNovaInstancia(e.target.value)}
                className="sm:col-span-3 dark:bg-gray-200"
                placeholder={t('channels.namePlaceholder')}
                disabled={isCreating || isEditing || editingInstance !== null}
              />
            </div>

            {/* Filas */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label className="sm:text-right dark:text-gray-200">
                {t('channels.queuesLabel')}
              </Label>
              <SelectWithSearch
                options={queueOptions}
                value={selectedQueues}
                onChange={setSelectedQueues}
                placeholder={t('channels.queuesPlaceholder')}
                disabled={isCreating || isEditing}
                emptyMessage={t('channels.noQueueFound')}
                className="sm:col-span-3"
              />
            </div>

            {/* Usuários */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4">
              <Label className="sm:text-right dark:text-gray-200">
                {t('channels.usersLabel')}
              </Label>
              <SelectWithSearch
                options={userOptions}
                value={selectedUsers}
                onChange={setSelectedUsers}
                placeholder={t('channels.usersPlaceholder')}
                disabled={isCreating || isEditing}
                emptyMessage={t('channels.noUserFound')}
                className="sm:col-span-3"
              />
              <p className="col-start-2 col-span-3 -mt-2 text-xs text-muted-foreground">
                {selectedUsers.length > 0 ? t('channels.usersRestrictedHint') : t('channels.usersOpenHint')}
              </p>
            </div>
            {tipoApi === 'META' ? (
              <div className="flex flex-col gap-4">
                {/* Seletor Automático/Manual oculto: somos Tech Provider, o fluxo padrão é o Automático.
                    O modo manual só aparece ao editar uma instância ou se a criação automática falhar. */}
                {metaMode === 'auto' ? (
                  <MetaEmbeddedSignup 
                    onSuccess={({ displayPhoneNumber, verifiedName, ...metaData }) => {
                      setMetaConfig((prev: any) => ({ ...prev, ...metaData }));
                      // Opção 1: "Conectar" já cria a instância. Sem os três
                      // dados (login falhou), permanece no fluxo automático para tentar de novo.
                      if (metaData.accessToken && metaData.wabaId && metaData.phoneNumberId) {
                        const nome = novaInstancia.trim() || verifiedName || displayPhoneNumber || 'WhatsApp';
                        if (!novaInstancia.trim()) setNovaInstancia(nome);
                        void handleCreateInstance({ meta: metaData, nome });
                      }
                    }} 
                    disabled={isCreating || isEditing}
                  />
                ) : (
                  <MetaInstanceForm 
                    formData={metaConfig} 
                    setFormData={setMetaConfig} 
                    disabled={isCreating || isEditing}
                    uazapiInstances={instancias.filter(i => i.tipoApi === 'UAZAPI').map(i => ({ id: i.id, nome: i.nome, statusConexao: i.statusConexao || 'disconnected' }))}
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* API URL */}
                <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4 hidden">
                  <Label htmlFor="apiUrl" className="sm:text-right dark:text-gray-200">
                    {tipoApi === 'UAZAPI' ? 'UAZAPI API URL' : 'EVOLUTION API URL'}
                  </Label>
                  <Input
                    id="apiUrl"
                    value={apiUrl}
                    onChange={(e) => setApiUrl(e.target.value)}
                    className="sm:col-span-3 dark:bg-gray-200"
                    placeholder={tipoApi === 'UAZAPI' ? 'https://seu-subdominio.uazapi.com' : 'https://sua-evolution-api.com'}
                    disabled={isCreating || isEditing || editingInstance !== null}
                  />
                </div>
                {/* Admin Token / API Key: não há campo — o backend usa UAZAPI_API_KEY / EVOLUTION_API_KEY do
                    ambiente. Um <input type="password"> oculto aqui era preenchido pelo autofill do navegador
                    com a senha salva do usuário e enviado como apiKey (causava 401 na Uazapi). */}
            {/* Webhook URL - OCULTO - é fixo, não deve ser editável */}
            {/* Events switches */}
            {/* <div className="grid grid-cols-4 items-start gap-4">
              <Label className="sm:text-right dark:text-gray-200">Eventos</Label>
              <div className="sm:col-span-3 space-y-2">
                {defaultEvents.map((evt) => (
                  <div key={evt} className="flex items-center justify-between border rounded px-3 py-2">
                    <span className="text-sm dark:text-gray-200">{evt}</span>
                    <Switch
                      checked={eventSwitches[evt]}
                      onCheckedChange={(val) => setEventSwitches((prev) => ({ ...prev, [evt]: !!val }))}
                      disabled={isCreating || isEditing}
                    />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground dark:text-gray-200">Todos os eventos vêm desativados por padrão. Ative os desejados.</p>
              </div>
            </div> */}
            </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpenDialog(false)}
              disabled={isCreating || isEditing}
              className="dark:text-gray-200"
            >
              {t('common.cancel')}
            </Button>
            <HintTooltip text={t('channels.hints.createInstance')}>
            <Button
              onClick={(e) => {
                e.preventDefault();
                if (editingInstance) {
                  handleUpdateInstance();
                } else {
                  handleCreateInstance();
                }
              }}
              disabled={isCreating || isEditing || !novaInstancia.trim()}
            >

              {isCreating || isEditing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isCreating ? t('common.saving') : t('channels.editing')}
                </>
              ) : (editingInstance ? t('channels.editInstance') : t('channels.createInstance'))}
            </Button>
            </HintTooltip>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrCodeDialog} onOpenChange={setQrCodeDialog}>
        <DialogContent className="sm:max-w-md dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="dark:text-gray-200">{t('channels.connectWhatsappTitle')}</DialogTitle>
            <DialogDescription className="dark:text-gray-200">
              {t('channels.scanQrDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center py-4">
            <div className="p-4 border rounded-lg bg-white dark:bg-gray-900 dark:border-gray-700">
              {currentQrCode ? (
                <img
                  src={currentQrCode}
                  alt="QR Code"
                  className="w-full max-w-[260px] h-auto"
                />
              ) : (
                <div className="flex items-center justify-center w-[260px] h-[260px]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
            </div>

            <p className="mt-4 text-sm text-muted-foreground text-center dark:text-gray-200">
              {t('channels.openWhatsappInstructions_pre')} <strong>{t('channels.moreOptions')}</strong> &gt;
              <strong>{t('channels.linkedDevices')}</strong> &gt; <strong>{t('channels.linkDevice')}</strong>
            </p>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={handleRefreshQrCode}
              disabled={!!isConnecting}
              className="dark:text-gray-200"
            >
              {isConnecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t('channels.refreshQrCode')}
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => setQrCodeDialog(false)}
              className="dark:text-gray-200 bg-gray-200 dark:bg-gray-700"
            >
              <Check className="mr-2 h-4 w-4" />
              {t('common.understood')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {templateManagerInstanceId && (
        <MetaTemplateManager
          instanciaId={templateManagerInstanceId}
          open={!!templateManagerInstanceId}
          onClose={() => setTemplateManagerInstanceId(null)}
        />
      )}

      {businessProfileInstanceId && (
        <MetaBusinessProfileDialog
          instanciaId={businessProfileInstanceId}
          open={!!businessProfileInstanceId}
          onClose={() => setBusinessProfileInstanceId(null)}
        />
      )}

      {webhookInstanceId && (
        <MetaWebhookDialog
          instanciaId={webhookInstanceId}
          open={!!webhookInstanceId}
          onClose={() => setWebhookInstanceId(null)}
        />
      )}
    </div>
  );
};

export default InstanceApiConnection;
